import { useState, useEffect } from "react";

export interface ButtonConfig {
  text: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  icon?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

interface ContentHeaderProps {
  title: string;
  subtitle?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  buttons?: ButtonConfig[];
  showSearch?: boolean;
  children?: React.ReactNode;
  className?: string;
  // Responsive component slots
  mobileComponents?: React.ReactNode;
  desktopComponents?: React.ReactNode;
}

const ContentHeader: React.FC<ContentHeaderProps> = ({
  title,
  subtitle,
  searchPlaceholder = "Search listings by name, SKU...",
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
  buttons = [],
  showSearch = true,
  children,
  className = "",
  mobileComponents,
  desktopComponents,
}) => {
  const [localSearch, setLocalSearch] = useState(searchValue);

  // Use the buttons array directly
  const allButtons: ButtonConfig[] = buttons;

  // Sync local search with prop value
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);
    onSearchChange?.(value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchSubmit?.(localSearch);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSearchSubmit?.(localSearch);
    }
  };

  const renderButton = (button: ButtonConfig, index: number) => {
    // Use custom data-testid if provided, otherwise generate from text
    const getButtonTestId = (text: string) => {
      if (text === "Add Product Merchant") return "add-product-button";
      if (text === "Import Products") return "import-products-button";
      if (text === "Export Products") return "export-products-button";
      // Default generation for other buttons
      return (
        text
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "") + "-button"
      );
    };
    const testId = button["data-testid"] || getButtonTestId(button.text);

    return (
      <button
        key={index}
        data-testid={testId}
        className={`tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-transition-colors tw-whitespace-nowrap ${
          button.variant === "primary"
            ? "tw-bg-primary tw-text-white hover:tw-bg-primary/90"
            : "tw-bg-slate-200/60 dark:tw-bg-slate-800/60 tw-text-slate-800 dark:tw-text-slate-200 hover:tw-bg-slate-200 dark:hover:tw-bg-slate-800"
        } ${button.disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""} 
        tw-h-8 tw-px-3 md:tw-h-10 md:tw-px-4 tw-text-xs md:tw-text-sm tw-font-medium md:tw-font-bold md:tw-tracking-wide
        ${button.className ?? ""}
        `}
        onClick={button.onClick}
        disabled={button.disabled}
      >
        {button.icon && (
          <span className="material-symbols-outlined tw-text-sm md:tw-text-base tw-truncate">
            {button.icon}
          </span>
        )}
        <span className="tw-truncate">{button.text}</span>
      </button>
    );
  };

  return (
    <header
      data-testid="page-header"
      className={`tw-shadow-sm tw-border-b tw-bg-white dark:tw-bg-background-dark tw-border-slate-200 dark:tw-border-slate-700 ${className}`}
    >
      {/* Mobile Layout */}
      <div className="tw-block md:tw-hidden" data-testid="mobile-header">
        {/* Mobile Centered Title */}
        <div className="tw-flex tw-items-center tw-justify-center tw-pt-3 tw-pb-0">
          <div className="tw-text-center">
            <h1
              className="tw-text-lg tw-font-bold tw-text-slate-900 dark:tw-text-white"
              data-testid="mobile-title"
            >
              {title}
            </h1>
            {subtitle && (
              <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400 tw-mt-1">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Mobile Search Section */}
        {showSearch && (
          <div
            className="tw-px-4 tw-py-3"
            data-testid="mobile-search-container"
          >
            <form onSubmit={handleSearchSubmit} className="tw-block">
              <label className="tw-flex tw-h-10 tw-w-full tw-flex-col">
                <div className="tw-flex tw-h-full tw-w-full tw-flex-1 tw-items-stretch tw-rounded-lg tw-bg-slate-200/60 dark:tw-bg-slate-800/60">
                  <div className="tw-flex tw-items-center tw-justify-center tw-pl-3 tw-text-slate-500 dark:tw-text-slate-400">
                    <span className="material-symbols-outlined tw-text-[20px]">
                      search
                    </span>
                  </div>
                  <input
                    data-testid="product-search-input"
                    aria-label="search"
                    className="form-input tw-h-full tw-min-w-0 tw-flex-1 tw-resize-none tw-overflow-hidden tw-rounded-r-lg tw-border-none tw-bg-transparent tw-px-2 tw-text-sm tw-font-normal tw-leading-normal tw-text-slate-900 placeholder:tw-text-slate-500 focus:tw-outline-0 focus:tw-ring-0 dark:tw-text-white dark:placeholder:tw-text-slate-400"
                    placeholder={searchPlaceholder}
                    value={localSearch}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </label>
            </form>
          </div>
        )}

        {/* Mobile Buttons Section */}
        {allButtons.length > 0 && (
          <div
            className="tw-flex tw-gap-3 tw-overflow-x-auto tw-px-4 tw-pb-3 tw-scrollbar-hide"
            data-testid="action-buttons-container"
          >
            {allButtons.map(renderButton)}
          </div>
        )}

        {/* Mobile-specific Components */}
        {mobileComponents && (
          <div className="tw-px-4 tw-pb-3">{mobileComponents}</div>
        )}
      </div>

      {/* Desktop Layout */}
      <div
        className="tw-hidden md:tw-flex md:tw-shrink-0 md:tw-items-center md:tw-justify-between md:tw-px-6 md:tw-py-4"
        data-testid="desktop-header"
      >
        {/* Desktop Title Section */}
        <div className="tw-flex tw-flex-1 tw-items-center tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <h1
              className="tw-text-xl tw-font-bold tw-text-gray-900 dark:tw-text-white"
              data-testid="merchant-page-title"
            >
              {title}
            </h1>
            {subtitle && (
              <p className="tw-hidden tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Desktop Search & Buttons Section */}
        <div
          className="tw-flex tw-items-center tw-gap-4"
          data-testid="desktop-search-section"
        >
          {/* Desktop Search */}
          {showSearch && (
            <form
              onSubmit={handleSearchSubmit}
              className="tw-relative tw-flex tw-w-full tw-min-w-[274px] tw-max-w-md tw-items-stretch"
              data-testid="search-container"
            >
              <div className="tw-text-gray-500 tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-justify-center tw-pl-3">
                <span className="material-symbols-outlined tw-text-base">
                  search
                </span>
              </div>
              <input
                data-testid="product-search-input"
                aria-label={searchPlaceholder}
                className="tw-form-input tw-flex tw-w-full tw-min-w-0 tw-flex-1 tw-resize-none tw-overflow-hidden tw-rounded-lg tw-border-gray-300 tw-bg-white dark:tw-bg-gray-800 tw-h-10 tw-pl-10 tw-pr-4 tw-text-sm tw-font-normal tw-leading-normal tw-text-gray-800 dark:tw-text-gray-200 placeholder:tw-text-gray-500 dark:placeholder:tw-text-gray-400 focus:tw-outline-0 focus:tw-ring-2 focus:tw-ring-primary/50 dark:tw-border-gray-600"
                placeholder={searchPlaceholder}
                value={localSearch}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
            </form>
          )}

          {/* Desktop Buttons */}
          {allButtons.length > 0 && (
            <div
              className="tw-flex tw-gap-4"
              data-testid="action-buttons-container"
            >
              {allButtons.map(renderButton)}
            </div>
          )}
        </div>
      </div>

      {/* Desktop-specific Components */}
      {desktopComponents && (
        <div className="tw-hidden md:tw-block tw-border-t tw-border-slate-200 dark:tw-border-slate-700">
          {desktopComponents}
        </div>
      )}

      {/* Children Content (Store filters, status tabs, etc.) - Both Mobile & Desktop */}
      {children}
    </header>
  );
};

export default ContentHeader;
